import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CustomFieldsDialog — hallitse raporttitason "Muut kerättävät tiedot" -kenttiä.
 *
 * Kentät tallennetaan raportille report.custom_fields = [{id, label, type, options, hint}].
 * Asentaja täyttää arvot yksikkökohtaisesti mobiilinäkymässä (unit.custom_values).
 *
 * Suunnittelu:
 * - Yksinkertainen lista, ei drag&drop toistaiseksi (voidaan lisätä myöhemmin).
 * - Kentän tyypit: text, number, select. Select vaatii vähintään 1 vaihtoehdon.
 * - Tallennus tapahtuu vasta "Tallenna" -painikkeen kautta — dialogissa
 *   käyttäjä voi muokata vapaasti ilman auto-savea, jotta ei synny välitilassa
 *   olevia kenttiä (esim. select ilman vaihtoehtoja).
 */
export default function CustomFieldsDialog({ report, onClose, onSaved }) {
  const [fields, setFields] = useState(() =>
    (report.custom_fields || []).map((f) => ({
      id: f.id,
      label: f.label || "",
      type: f.type || "text",
      options: Array.isArray(f.options) ? [...f.options] : [],
      hint: f.hint || "",
      _optionsText: (f.options || []).join("\n"),
    })),
  );
  const [saving, setSaving] = useState(false);

  const addField = () => {
    const id = Math.random().toString(36).slice(2, 12);
    setFields((prev) => [
      ...prev,
      { id, label: "", type: "text", options: [], hint: "", _optionsText: "" },
    ]);
  };

  const updateField = (id, patch) =>
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    );

  const removeField = (id) =>
    setFields((prev) => prev.filter((f) => f.id !== id));

  const save = async () => {
    // Validointi: label pakollinen, select tarvitsee ≥1 vaihtoehdon
    const cleaned = [];
    for (const f of fields) {
      const label = (f.label || "").trim();
      if (!label) {
        toast.error("Anna nimi kaikille kentille");
        return;
      }
      const options = f.type === "select"
        ? (f._optionsText || "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      if (f.type === "select" && options.length === 0) {
        toast.error(`Lisää vähintään yksi vaihtoehto kenttään "${label}"`);
        return;
      }
      cleaned.push({
        id: f.id,
        label,
        type: f.type,
        options,
        hint: (f.hint || "").trim(),
      });
    }
    setSaving(true);
    try {
      await onSaved(cleaned);
      onClose();
    } catch {
      toast.error("Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      data-testid="custom-fields-dialog"
    >
      <div className="bg-slate-50 w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[96vh]">
        {/* Header */}
        <div className="flex items-start justify-between p-6 md:p-7 pb-4 flex-shrink-0 bg-white md:rounded-t-3xl rounded-t-3xl border-b border-slate-100">
          <div className="min-w-0 pr-3">
            <div className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold mb-1">
              Muut kerättävät tiedot
            </div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">
              Rakenna yrityksesi omat kentät
            </h2>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              Nämä kentät näkyvät asentajan puhelimessa jokaisen yksikön alla.
              Voit kerätä esimerkiksi <span className="text-slate-700 font-medium">Käyttötunnit</span>, <span className="text-slate-700 font-medium">Kesä-/talvipellin asento</span>, <span className="text-slate-700 font-medium">Huoltoluokka</span> tai <span className="text-slate-700 font-medium">Venttiilityyppi</span>.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 flex-shrink-0"
            data-testid="custom-fields-close"
            aria-label="Sulje"
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 md:py-6 space-y-3">
          {fields.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="custom-fields-empty">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 mb-3 ring-1 ring-sky-100">
                <Plus className="h-6 w-6" />
              </div>
              <div className="text-sm font-semibold text-slate-900">Ei kenttiä vielä</div>
              <div className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
                Aloita rakentamalla ensimmäinen kenttä alla.
              </div>
            </div>
          )}
          {fields.map((f) => (
            <FieldEditor
              key={f.id}
              field={f}
              onChange={(patch) => updateField(f.id, patch)}
              onRemove={() => removeField(f.id)}
            />
          ))}

          {/* Add field — iso rakennuspalikkanappi */}
          <button
            type="button"
            onClick={addField}
            className="w-full mt-2 flex items-center gap-4 px-5 py-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/40 text-left transition-colors group"
            data-testid="custom-fields-add"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100 group-hover:bg-sky-100 flex-shrink-0">
              <Plus className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm md:text-base text-slate-900">Lisää uusi kenttä</div>
              <div className="text-xs text-slate-500 mt-0.5">Luo uusi tieto, jonka asentaja täyttää työmaalla.</div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 md:p-5 border-t border-slate-200 bg-white md:rounded-b-3xl flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100"
            data-testid="custom-fields-cancel"
          >
            Peruuta
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 shadow-sm"
            data-testid="custom-fields-save"
          >
            {saving ? "Tallennetaan…" : "Tallenna"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldEditor({ field, onChange, onRemove }) {
  const typeInfo = {
    text: { label: "Teksti", icon: "Aa" },
    number: { label: "Numero", icon: "12" },
    select: { label: "Pudotusvalikko", icon: "▾" },
  }[field.type] || { label: "Teksti", icon: "Aa" };
  const displayName = (field.label || "").trim() || "Uusi kenttä";

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 space-y-4"
      data-testid={`custom-field-${field.id}`}
    >
      {/* Card header — nimi kortin otsikkona */}
      <header className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-100 font-semibold text-sm flex-shrink-0">
          {typeInfo.icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`font-display text-base md:text-lg font-semibold truncate ${(field.label || "").trim() ? "text-slate-900" : "text-slate-400 italic"}`}>
            {displayName}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">
            {typeInfo.label}
          </div>
        </div>
        <button
          onClick={onRemove}
          className="p-2 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
          data-testid={`custom-field-remove-${field.id}`}
          aria-label="Poista kenttä"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </header>

      {/* Label */}
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
          Kentän nimi
        </label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="esim. Kesä-/talvipellin asento"
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-sm"
          data-testid={`custom-field-label-${field.id}`}
        />
      </div>

      {/* Type — kolme selkeää valintaa */}
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
          Tyyppi
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: "text", label: "Teksti", icon: "Aa" },
            { key: "number", label: "Numero", icon: "12" },
            { key: "select", label: "Pudotusvalikko", icon: "▾" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange({ type: t.key })}
              className={`px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-colors flex items-center justify-center gap-1.5 ${
                field.type === t.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`custom-field-type-${t.key}-${field.id}`}
            >
              <span className={`text-xs font-mono ${field.type === t.key ? "opacity-80" : "text-slate-400"}`}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Options (only for select) */}
      {field.type === "select" && (
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Vaihtoehdot <span className="text-slate-400 font-normal normal-case tracking-normal">(yksi per rivi)</span>
          </label>
          <textarea
            value={field._optionsText}
            onChange={(e) => onChange({ _optionsText: e.target.value })}
            rows={3}
            placeholder="Kesä&#10;Talvi&#10;Automaattinen"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-sm font-mono"
            data-testid={`custom-field-options-${field.id}`}
          />
        </div>
      )}

      {/* Hint */}
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
          Ohjeteksti asentajalle <span className="text-slate-400 font-normal normal-case tracking-normal">(valinnainen)</span>
        </label>
        <input
          type="text"
          value={field.hint}
          onChange={(e) => onChange({ hint: e.target.value })}
          placeholder="esim. Kirjaa tehdas- tai huoltoarvo"
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-sm"
          data-testid={`custom-field-hint-${field.id}`}
        />
      </div>
    </div>
  );
}
